/**
 * Which catalog plans the self-serve surfaces may OFFER.
 *
 * Grandfathering: when a tier is retired, the accounts already on it keep it —
 * their price and allowances are unchanged and nothing moves them off — but it
 * must stop appearing as something to switch TO. The backend catalog is the
 * wrong place to express that: `subscription_plans.self_serve = 0` would also
 * strip the account's own billing surfaces (rate card, checkout, seat and
 * schema add-ons all branch on it), and deleting the row would break the
 * account that still references it.
 *
 * So it is a deployment-time UI setting: `REACT_APP_HIDDEN_PLAN_CODES`, a
 * comma-separated list of plan codes. Unset (the default) hides nothing.
 *
 * Deliberately NOT a per-account switch. "Which tiers do we sell today" is a
 * property of the deployment, the same shape as `REACT_APP_SHOW_SUBSCRIPTION`;
 * per-account entitlement already has a home in `plan_addon_defaults`.
 *
 * ⚠️ CRA inlines `process.env` at BUILD time, so changing this needs a rebuild,
 * not a restart. On Netlify that is a redeploy of the UI.
 */
import { TCatalogPlan } from 'src/api/functions/subscriptionApi';
import { TSubscription } from 'src/types/TSubscription';
import { config } from 'src/utils/config';

/**
 * Is this account on a self-serve plan?
 *
 * Enterprise / negotiated plans (`CUSTOM-…`, `self_serve = 0`) are invoiced
 * against a contract: their price, allowances and entitlements are bespoke, so
 * every self-serve billing surface is wrong for them — list rates they are not
 * charged, a tier comparison they cannot buy from, and a card-capture checkout
 * for a plan that has no Stripe price at all (which is why "Add payment
 * method" failed on CUSTOM). Those surfaces are hidden rather than shown
 * broken.
 *
 * Lives here (not in Subscription.tsx) so leaf components like BillingBanner
 * can import it without closing the Subscription → components barrel →
 * AppShell → BillingBanner cycle, which the build rejects.
 */
export const isSelfServePlan = (subscription: TSubscription): boolean =>
  Boolean(subscription.details.self_serve) && !subscription.plan.startsWith('CUSTOM-');

/**
 * Where an account goes when the self-serve ladder has no answer for it.
 *
 * Lives beside the visibility rules because it is the other half of them: the
 * moment a tier is withheld, something has to say where to go instead, and
 * three surfaces now need the same destination — the picker's footnote, the
 * picker's "nothing to switch to" banner, and the plan-gate upsell whose
 * required tier has been retired. External and opened in a new tab: the reader
 * is mid-task, and navigating the app away would lose the context they came
 * with.
 */
export const ENTERPRISE_CONTACT_URL = 'https://filespin.io/book-a-demo';

/**
 * Is `code` hidden from the offer by configuration?
 *
 * Case-insensitive: the codes are typed by hand into an env var, and a
 * deployment that wrote `starter` instead of `STARTER` would otherwise fail
 * silently — still showing the plan it meant to retire, with nothing anywhere
 * to indicate why.
 */
export const isPlanHidden = (code: string): boolean =>
  config.hiddenPlanCodes.includes(String(code || '').trim().toUpperCase());

/**
 * The plans a self-serve surface may offer, in catalog order.
 *
 * Trial tiers are dropped here too — you cannot "upgrade" to one — so that
 * every caller gets the same answer to "what can this account switch to"
 * rather than each re-deriving half of it.
 *
 * The account's CURRENT plan is not special-cased in: a retired plan stays out
 * of the offer even for the account on it, because everything the picker
 * derives from this list describes the offer. Splicing a legacy tier back into
 * the ladder would compute each card's "plus …" delta against it and shrink the
 * "All plans include" intersection to whatever that one tier happens to lack —
 * misdescribing the current line-up to precisely the customer we are trying to
 * treat well. `hiddenCurrentPlan` below is how the picker tells that customer
 * where they stand instead.
 */
export const offeredPlans = (
  plans: TCatalogPlan[] | undefined | null,
): TCatalogPlan[] =>
  (plans ?? []).filter((p) => !p.is_trial && !isPlanHidden(p.code));

/**
 * The account's own plan, but only when configuration has taken it out of the
 * offer — i.e. this account is grandfathered on a retired tier. Null in every
 * other case, including when the code isn't in the catalog at all (Enterprise,
 * or a plan pulled from the catalog entirely), which the picker already covers
 * with its non-self-serve branch.
 *
 * The picker uses this for one line of copy: without it, a grandfathered
 * customer opens "Change plan", does not see the plan they are on, and has to
 * guess whether it still exists.
 */
export const hiddenCurrentPlan = (
  plans: TCatalogPlan[] | undefined | null,
  currentCode: string | undefined | null,
): TCatalogPlan | null => {
  if (!currentCode || !isPlanHidden(currentCode)) return null;
  return (plans ?? []).find((p) => p.code === currentCode) ?? null;
};
